import React, { useId } from 'react';
import { formatCurrency } from '../utils/format';
import useElasticHover from '../hooks/useElasticHover';

const W = 240;
const H = 45;
const PAD = 4;

// Desenha a série real: eixo Y normalizado entre o mínimo e o máximo do período.
function buildSparkline(series) {
  const pts = (series || [])
    .map((v, i) => ({ v, i }))
    .filter(p => typeof p.v === 'number' && Number.isFinite(p.v));
  if (pts.length < 2) return null;
  const n = series.length - 1;
  const min = Math.min(...pts.map(p => p.v));
  const max = Math.max(...pts.map(p => p.v));
  const range = max - min;
  const coords = pts.map(p => {
    const x = (p.i / n) * W;
    const y = range === 0 ? H / 2 : PAD + (1 - (p.v - min) / range) * (H - PAD * 2);
    return [x.toFixed(1), y.toFixed(1)];
  });
  const line = coords.map(([x, y], i) => `${i ? 'L' : 'M'} ${x} ${y}`).join(' ');
  const area = `${line} L ${coords[coords.length - 1][0]} ${H} L ${coords[0][0]} ${H} Z`;
  return { line, area };
}

export default function KPICard({
  label,
  value,
  footer,
  isPercent = false,
  loading = false,
  trend = null,
  trendPositive = true,
  trendTitle = 'vs mês anterior',
  series = null,
  color = '#4A6FD8',
  customDisplay = null,
  headerAction = null,
}) {
  const gradId = `kpi-grad-${useId().replace(/:/g, '')}`;
  const elasticRef = useElasticHover();
  const numVal = typeof value === 'number' ? value : parseFloat(value) || 0;
  const displayValue = customDisplay !== null
    ? customDisplay
    : isPercent
    ? `${numVal.toFixed(1)}%`
    : formatCurrency(numVal);

  const spark = buildSparkline(series);
  const isPositive = trendPositive;

  if (loading) {
    return (
      <div className="bg-white rounded-[22px] p-5 shadow-[0_10px_30px_-8px_rgba(112,144,176,0.12)] border border-white/80 space-y-3">
        <div className="flex justify-between items-center">
          <div className="h-3 bg-slate-100 rounded-full w-24 animate-pulse" />
          <div className="h-5 bg-slate-100 rounded-full w-12 animate-pulse" />
        </div>
        <div className="h-7 bg-slate-100 rounded-lg w-32 animate-pulse" />
        <div className="h-8 bg-slate-50 rounded-xl w-full animate-pulse" />
      </div>
    );
  }

  return (
    <div
      ref={elasticRef}
      className="group bg-white rounded-[22px] p-5 shadow-[0_12px_32px_-10px_rgba(112,144,176,0.14)] hover:shadow-[0_18px_38px_-10px_rgba(112,144,176,0.22)] border border-white/90 transition-shadow duration-300 relative overflow-hidden flex flex-col justify-between"
      data-testid={`kpi-card-${label?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Top row: Label + Trend Badge / Header Action */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[13px] font-semibold text-slate-600 tracking-tight">
          {label}
        </span>
        <div className="flex items-center gap-1.5">
          {headerAction}
          {trend && (
            <span
              title={trendTitle}
              className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-bold tracking-tight whitespace-nowrap ${
                isPositive
                  ? 'bg-[#E8F8F0] text-[#10B981] border border-[#D1F2E2]'
                  : 'bg-[#FDF0EE] text-[#EF4444] border border-[#FCDAD6]'
              }`}
            >
              {isPositive ? (
                <span className="text-[9px]">▲</span>
              ) : (
                <span className="text-[9px]">▼</span>
              )}
              <span>{trend.replace(/[+-]/g, '')}</span>
            </span>
          )}
        </div>
      </div>

      {/* Main Big Value */}
      <div className="mb-2">
        <p
          className="text-2xl font-bold tracking-tight text-slate-800"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          {displayValue}
        </p>
        {footer && (
          <p className="text-[11px] text-slate-400 truncate mt-0.5">{footer}</p>
        )}
      </div>

      {spark && (
        <div className="w-full h-10 -mb-2 mt-auto overflow-hidden pointer-events-none">
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="none">
            <defs>
              <linearGradient id={gradId} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={color} stopOpacity="0.28" />
                <stop offset="100%" stopColor={color} stopOpacity="0.0" />
              </linearGradient>
            </defs>
            <path d={spark.area} fill={`url(#${gradId})`} />
            <path
              d={spark.line}
              fill="none"
              stroke={color}
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>
      )}
    </div>
  );
}

